import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Vehicle } from '@hero/schema';
import { SharedTable, type StatusBadgeConfigType } from '../../../app/Table/SharedTable';
import type { SearchInputProps } from '../../../app/Search/SearchInput';
import { VehicleRow } from './VehicleRow';

export interface VehicleStatusFilterConfig {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  options: Array<{
    value: string;
    label: string;
    count?: number;
    className?: string;
  }>;
  counts?: Array<{ value: string; count: number }>;
}

export interface VehicleTableFilters {
  showSearch?: boolean;
  statusFilter?: VehicleStatusFilterConfig;
}

export interface VehicleTableProps {
  vehicles: Vehicle[];
  isLoading?: boolean;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
  searchProps?: SearchInputProps;
  onSort?: (column: string) => void;
  onLoadMore?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  filters?: VehicleTableFilters;
  /** Empty-state shown only when the list is empty AND no filters/search are active. */
  emptyState?: React.ReactNode;
  /** Set to `true` whenever the user has search / filter / sort applied. */
  hasActiveFilters?: boolean;
  children?: React.ReactNode;
}

const VEHICLE_STATUS_CLASSES: Record<string, string> = {
  active: 'bg-success-50 text-success-400',
  pending: 'bg-warning-200 text-warning-500',
  busy: 'bg-warning-100 text-warning-400',
  inactive: 'bg-error-50 text-error-400',
};

const VEHICLE_STATUS_BADGE_CONFIG: StatusBadgeConfigType = {
  getStatusClass: (status: string) => VEHICLE_STATUS_CLASSES[status] ?? 'bg-gray-100 text-gray-600',
  getStatusDisplayText: (status: string) => status,
};

const VehicleTableComponent: React.FC<VehicleTableProps> = ({
  vehicles,
  isLoading,
  sortColumn,
  sortDirection = 'asc',
  searchProps,
  onSort,
  onLoadMore = () => {},
  hasNextPage = false,
  isFetchingNextPage = false,
  filters,
  emptyState,
  hasActiveFilters,
  children,
}) => {
  const { t } = useTranslation(['m-vehicle']);

  const columns = useMemo(
    () => [
      { key: 'vehicle', label: t('table.columns.vehicle'), sortable: false },
      { key: 'status', label: t('table.columns.status'), sortable: false },
      { key: 'driver', label: t('table.columns.driver'), sortable: false },
      { key: 'model', label: t('table.columns.model'), sortable: false },
      { key: 'actions', label: '', sortable: false },
    ],
    [t],
  );

  return (
    <SharedTable
      data={vehicles}
      isLoading={isLoading}
      sortColumn={sortColumn}
      sortDirection={sortDirection}
      searchProps={searchProps}
      onSort={onSort}
      statusBadgeConfig={VEHICLE_STATUS_BADGE_CONFIG}
      onLoadMore={onLoadMore}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      emptyState={emptyState}
      hasActiveFilters={hasActiveFilters}
      getRowId={(vehicle: Vehicle) => vehicle._id.toString()}>
      {children || <VehicleTableHead filters={filters} />}

      <SharedTable.Mid>
        <SharedTable.Rows
          columns={columns}
          rowRender={(vehicle: Vehicle) => <VehicleRow vehicle={vehicle} />}
        />
      </SharedTable.Mid>
    </SharedTable>
  );
};

const VehicleTableHead: React.FC<{ filters?: VehicleTableFilters }> = ({ filters }) => {
  if (!filters) return null;

  return (
    <SharedTable.Head>
      {filters.showSearch !== false && <SharedTable.Search />}
      {filters.statusFilter && (
        <SharedTable.Badge
          options={filters.statusFilter.options}
          counts={filters.statusFilter.counts}
          value={filters.statusFilter.value}
          onChange={filters.statusFilter.onChange}
          showCounts={!!filters.statusFilter.counts}
        />
      )}
    </SharedTable.Head>
  );
};

export const VehicleTable = Object.assign(VehicleTableComponent, {
  Head: SharedTable.Head,
  Search: SharedTable.Search,
  Badge: SharedTable.Badge,
  Mid: SharedTable.Mid,
  Rows: SharedTable.Rows,
});
